<template>
  <section class="section-headline">
    <div class="section-headline-inner">
      <div class="headline-inner">
        <h1>{{ $t(`${kindPrefix}.Title`) }}</h1>

        <mq-layout class="headline-switches" mq="lg">
          <b-field v-if="pageKind === LeadsPageKind.Leads">
            <b-tooltip position="is-bottom" type="is-dark">
              <b-switch :value="pageKind ? uniqueByCampaignClientLeads : uniqueByCampaign" @input="onUniqueByCampaignChange($event)">{{ $t(`${kindPrefix}.UniqueByCampaign`) }}</b-switch>
              <template #content>
                <p>{{ $t("Leads.Tooltips.Headline.UniqueByCampaign") }}</p>
              </template>
            </b-tooltip>
            <b-tooltip position="is-bottom" type="is-dark">
              <span class="chart-help">
                <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle /></icon-base>
              </span>
              <template #content>
                <p>{{ $t("Leads.UniqueTooltip") }}</p>
              </template>
            </b-tooltip>
          </b-field>
          <b-field v-if="showCampaignNameInLeads">
            <b-tooltip position="is-bottom" type="is-dark">
              <b-switch :value="pageKind ? showNotActiveCampaignClientLeads : showNotActiveCampaign" @input="onShowNotActiveCampaignChange($event)">{{ $t("Leads.ShowNotActiveCampaign") }}</b-switch>
              <template #content>
                <p>{{ $t("Leads.Tooltips.Headline.ShowNotActiveCampaign") }}</p>
              </template>
            </b-tooltip>            
          </b-field>
          <b-field>
            <b-tooltip position="is-bottom" type="is-dark">
              <b-switch :value="pageKind ? showDeletedClientLeads : showDeleted" @input="onShowDeletedChange($event)">{{ $t(`${kindPrefix}.Deleted`) }}</b-switch>
              <template #content>
                <p>{{ $t(`${kindPrefix}.Tooltips.Headline.Deleted`) }}</p>
              </template>
            </b-tooltip> 
          </b-field>
          <b-field>
            <b-tooltip position="is-bottom" type="is-dark">
              <b-switch :value="pageKind ? showFavouriteClientLeads : showFavourite" @input="onShowFavouriteChange($event)">{{ $t("Leads.Favourite") }}</b-switch>
              <template #content>
                <p>{{ $t("Leads.Tooltips.Headline.Favourite") }}</p>
              </template>
            </b-tooltip>
          </b-field>
          <b-field v-if="showGraphsPermission">
            <b-tooltip position="is-bottom" type="is-dark">
              <b-switch :value="pageKind ? !showGraphsClientLeads : !showGraphs" @input="onShowGraphsChange($event)" :disabled="deactivateStats">{{ $t("Leads.HideGraphs") }}</b-switch>
              <template #content>
                <p>{{ $t("Leads.Tooltips.Headline.HideGraphs") }}</p>
              </template>
            </b-tooltip>
          </b-field>
        </mq-layout>

        <mq-layout class="headline-filters-toggler" :mq="['sm', 'md']">
          <button type="button" class="is-transparent button-text-w" @click="showMobileFilters">
            <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
            <span class="text">{{ $t("Filters") }}</span>
          </button>
        </mq-layout>
      </div>

      <mq-layout class="headline-switches-m" :mq="['sm', 'md']">
        <b-field>
          <b-switch :value="uniqueByCampaign" @input="onUniqueByCampaignChange($event)">{{ $t("Leads.UniqueByCampaign") }}</b-switch>
        </b-field>
        <span class="border"></span>
        <b-field>
          <b-switch :value="pageKind ? !showGraphsClientLeads : !showGraphs" @input="onShowGraphsChange($event)">{{ $t("Leads.HideGraphs") }}</b-switch>
        </b-field>
      </mq-layout>
    </div>
    <mq-layout :mq="['sm', 'md']">
      <transition name="fade">
        <div v-show="mobileFilters" class="headline-filters-m" :class="{ active: mobileFilters }">
          <div class="headline-filters-m-container">
            <div class="headline-filters-m-h">
              <div class="headline-filters-m-title">
                <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
                <span class="text">{{ $t("Filters") }}</span>
              </div>
              <div class="headline-filters-m-close" @click="hideMobileFilters">
                <icon-base width="16" height="16" view-box="0 0 7 7" icon-name="times"><icon-times /></icon-base>
              </div>
            </div>
            <div class="headline-filters-m-b">
              <div class="headline-filters-m-switches">
                <b-field>
                  <b-switch :value="showNotActiveCampaign" @input="onShowNotActiveCampaignChange($event)">{{ $t("Leads.ShowNotActiveCampaign") }}</b-switch>
                </b-field>
                <b-field>
                  <b-switch @input="onShowFavouriteChange($event)">{{ $t("Leads.Favourite") }}</b-switch>
                </b-field>
              </div>
              <div class="headline-filters-m-filters">
                <LeadsFiltersDropdowns :pageKind="pageKind" :kindPrefix="kindPrefix"/>
                <LeadsFiltersDate :pageKind="pageKind"/>

                <div class="input-w">
                  <b-field class="search-container">
                    <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="search"><icon-search /></icon-base>
                    <b-input :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="buttons">
                <b-button type="is-primary" expanded @click="onFilterClick()">{{ $t("Filter") }}</b-button>
              </div>
            </div>
          </div>
          <div class="headline-filters-m-bg"></div>
        </div>
      </transition>
    </mq-layout>
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import LeadsFiltersDropdowns from "@/components/Leads/LeadsFiltersDropdowns.vue";
import LeadsFiltersDate from "@/components/Leads/LeadsFiltersDate.vue";
import { LeadsPageKind } from "@/models/LeadsPageKind";
import { LeadsPageViewModel, RefreshGridMode } from "@/models/leads/Lead";
import { CompanyPermission, LeadPermissionType } from "@/models/leads/CompanyPermission";
import IconBase from "../IconBase.vue";
import IconQuestionCircle from "../icons/IconQuestionCircle.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconTimes from "../icons/IconTimes.vue";
import IconUserSearch from "../icons/IconUserSearch.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "leadsModule";

@Component({
  components: {
    IconBase,
    IconQuestionCircle,
    IconSearch,
    IconTimes,
    IconUserSearch,
    LeadsFiltersDate,
    LeadsFiltersDropdowns,
  },
})
export default class LeadsHeadline extends Vue {
  @Prop({ required: true }) readonly pageKind!: LeadsPageKind;
  @Prop({ required: true }) readonly kindPrefix!: string;
  //#region Vuex getters and actions

  @Getter("deactivateStats", { namespace }) deactivateStats!: boolean;
  @Getter("leadsPageData", { namespace }) leadsPageData!: LeadsPageViewModel;
  @Getter("showDeleted", { namespace }) showDeleted!: boolean;
  @Getter("showDeletedClientLeads", { namespace }) showDeletedClientLeads!: boolean;
  @Getter("showFavourite", { namespace }) showFavourite!: boolean;
  @Getter("showFavouriteClientLeads", { namespace }) showFavouriteClientLeads!: boolean;
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("showGraphsClientLeads", { namespace }) showGraphsClientLeads!: boolean;
  @Getter("showNotActiveCampaign", { namespace }) showNotActiveCampaign!: boolean;
  @Getter("showNotActiveCampaignClientLeads", { namespace }) showNotActiveCampaignClientLeads!: boolean;
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Getter("uniqueByCampaignClientLeads", { namespace }) uniqueByCampaignClientLeads!: boolean;
  @Action("setNeedToRefreshLeadsGrid", { namespace }) setNeedToRefreshLeadsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;
  @Action("setShowDeleted", { namespace }) setShowDeleted!: (showDeleted: boolean) => void;
  @Action("setShowDeletedClientLeads", { namespace }) setShowDeletedClientLeads!: (showDeleted: boolean) => void;
  @Action("setShowFavourite", { namespace }) setShowFavourite!: (showFavourite: boolean) => void;
  @Action("setShowFavouriteClientLeads", { namespace }) setShowFavouriteClientLeads!: (showFavourite: boolean) => void;
  @Action("setShowGraphs", { namespace }) setShowGraphs!: (showGraphs: boolean) => void;
  @Action("setShowGraphsClientLeads", { namespace }) setShowGraphsClientLeads!: (showGraphs: boolean) => void;
  @Action("setShowNotActiveCampaign", { namespace }) setShowNotActiveCampaign!: (showNotActiveCampaign: boolean) => void;
  @Action("setShowNotActiveCampaignClientLeads", { namespace }) setShowNotActiveCampaignClientLeads!: (showNotActiveCampaign: boolean) => void;
  @Action("setUniqueByCampaign", { namespace }) setUniqueByCampaign!: (uniqueByCampaign: boolean) => void;
  @Action("setUniqueByCampaignClientLeads", { namespace }) setUniqueByCampaignClientLeads!: (uniqueByCampaign: boolean) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  public mobileFilters = false;

  LeadsPageKind = LeadsPageKind;

  //#endregion Properties


  //#region Getters

  get showCampaignNameInLeads(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.CampaignNameInLeads);
  }

  get showGraphsPermission(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.LeadsScreenCharts);
  }

  //#endregion Getters


  //#region Handlers

  public onFilterClick(): void {
    this.setNeedToRefreshLeadsGrid(RefreshGridMode.Full);
    this.hideMobileFilters();
  }

  public hideMobileFilters(): void {
    this.mobileFilters = false;
    this.popupBodyOff();
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  public onShowDeletedChange(showDeleted: boolean): void {
    if (this.pageKind){
      this.setShowDeletedClientLeads(showDeleted);
    } else {
      this.setShowDeleted(showDeleted);
    }
  }

  public onShowFavouriteChange(showFavourite: boolean): void {
    if (this.pageKind){
      this.setShowFavouriteClientLeads(showFavourite);
    } else {
      this.setShowFavourite(showFavourite);
    }
  }

  public onShowGraphsChange(hideGraphs: boolean): void {
    if (this.pageKind){
      this.setShowGraphsClientLeads(!hideGraphs);
    } else {
      this.setShowGraphs(!hideGraphs);
    }
  }

  public onShowNotActiveCampaignChange(showNotActiveCampaign: boolean): void {
    if (this.pageKind){
      this.setShowNotActiveCampaignClientLeads(showNotActiveCampaign);
    } else {
      this.setShowNotActiveCampaign(showNotActiveCampaign);
    }
  }

  public onUniqueByCampaignChange(uniqueByCampaign: boolean): void {
    if (this.pageKind){
      this.setUniqueByCampaignClientLeads(uniqueByCampaign);
    } else {
      this.setUniqueByCampaign(uniqueByCampaign);      
    }
  }

  public showMobileFilters(): void {
    this.mobileFilters = true;
    this.popupBodyOn();
  }

  //#endregion Handlers


  //#region Private methods

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-headline {
  // .section-headline-inner {
  //   position: fixed;
  //   top: 56px;
  //   right: 0;
  //   width: 100%;
  //   background: #f6f7fb;
  //   z-index: 99;
  //   height: 69px;
  //   @media (max-width: 991px) {
  //     height: 106px;
  //   }
  //   @media (max-width: 359px) {
  //     height: 135px;
  //   }
  // }

  .headline-inner {
    display: flex;
    align-items: center;
    padding: 0;
    height: 68px;
    border-bottom: 1px solid #d3d6e2;
    @media (max-width: 991px) {
      height: 52px;
    }
  }
  h1 {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    margin-bottom: 3px;
    @media (max-width: 991px) {
      font-size: 20px;
    }
  }

  .headline-switches {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    font-size: 16px;

    align-items: center;
    margin-right: auto;

    .field {
      margin: 3px 18px 3px 0;
      display: flex;

      &:first-child {
        // margin-right: 0;
      }

      .b-tooltip {
        color: #aaa;
        margin-left: .2rem;
        margin-right: 0;
      }
    }

    .switch {
      direction: ltr;
    }

    .check {
      direction: rtl;

      .control-label {
        white-space: nowrap;
      }
    }
  }
  .headline-filters-toggler {
    margin-right: auto;
    margin-left: -5px;

    @media (min-width: 992px) {
      display: none;
    }
    .button-text-w {
      transition: all 0.2s;
      &:hover {
        color: $primary;
      }
      .icon-w + .text {
        margin-right: 7px;
      }
    }
  }

  .headline-switches-m {
    display: flex;
    flex-wrap: wrap;
    font-size: 16px;
    align-items: center;
    padding: 10px 0;
    justify-content: flex-start;
    @media (max-width: 399px) {
      justify-content: space-between;
    }
    @media (max-width: 359px) {
      display: block;
    }
    .field {
      margin: 5px 0 5px 0;
      display: flex;
      position: relative;
    }
    .border {
      display: block;
      width: 1px;
      height: 16px;
      background: $lightd;
      margin: 0 30px;
      @media (max-width: 399px) {
        margin: 0 10px;
      }
      @media (max-width: 359px) {
        display: none;
      }
    }
    .switch {
      direction: ltr;
      @media (max-width: 359px) {
        width: 100%;
        justify-content: space-between;
      }
    }
    .check {
      direction: rtl;
      .control-label {
        white-space: nowrap;
      }
    }
  }

  .headline-filters-m {
    position: fixed;
    z-index: -1;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    .headline-filters-m-container {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 10;
      transition: all 0.5s;
      transform: translateX(0);
      background: #f6f7fb;
    }
    .headline-filters-m-h {
      position: fixed;
      top: 0;
      left: 0;
      height: 56px;
      width: 100%;
      color: #fff;
      display: flex;
      align-items: center;
      padding: 12px 15px 13px 10px;
      background: linear-gradient(90deg, $primary 0%, #1ca8d9 52.75%);
      .headline-filters-m-close {
        padding: 5px;
        cursor: pointer;
        line-height: 0;
        margin-right: auto;
        transition: all 0.2s;
        &:hover {
          opacity: 0.8;
        }
        .icon-w {
          svg {
          }
        }
      }
      .headline-filters-m-title {
        display: flex;
        align-items: center;
        font-size: 21px;
        .icon-w {
          margin-left: 10px;
        }
      }
    }
    .headline-filters-m-b {
      position: fixed;
      top: 56px;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 0 15px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      .headline-filters-m-switches {
        padding: 20px 0;
        border-bottom: 1px solid $lightd;
        .switch {
          width: 100%;
          justify-content: space-between;
        }
      }
      .headline-filters-m-filters {
        padding: 20px 0 0;
        display: flex;
        flex-wrap: wrap;
        margin: 0 -8px;
        .dropdown-w {
          padding: 0 8px 15px;
          .dropdown {
            width: 100%;
            .dropdown-trigger {
              width: 100%;
              .button {
                width: 100%;
              }
            }
          }
          &.datepicker-w {
            width: 100%;
            flex: 0 0 100%;
            .datepicker-w-i {
              position: relative;
            }
            .dropdown {
              width: 100%;
              .dropdown-trigger {
                width: 100%;
                .control {
                  width: 100%;
                }
              }
            }
          }
        }
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
          }
        }
      }
      .buttons {
        margin: auto 0 0;
      }
    }
    .headline-filters-m-bg {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 9;
      transition: all 0.5s;
      background: rgba(0, 0, 0, 0.3);
    }
    .buttons {
      padding-bottom: 30px;
      .button {
        margin: 0;
      }
    }

    &.fade-enter-active,
    &.fade-leave-active {
      z-index: 101;
      transition: all 0.5s;
    }
    &.fade-enter,
    &.fade-leave-to,
    &.fade-leave-active {
      opacity: 1;
      .headline-filters-m-container {
        transform: translateX(-100%);
      }
      .headline-filters-m-bg {
        opacity: 0;
      }
    }
    &.fade-enter {
    }
    &.active {
      z-index: 101;
      .headline-filters-m-container {
        // transform: translateX(0);
      }
    }
  }
}
html[dir="ltr"] {
  .section-headline {
    .headline-filters-toggler {
      margin: 0 -5px 0 auto;
      .button-text-w .icon-w + .text {
        margin: 0 0 0 7px;
      }
    }
    .headline-filters-m {
      .headline-filters-m-h {
        .headline-filters-m-close {
          margin: 0 0 0 auto;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
.section-headline {
  .headline-filters-m {
    .headline-filters-m-b {
      .headline-filters-m-filters {
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
            position: relative;
            display: block;
            .icon-w {
              position: absolute;
              top: 50%;
              right: 8px;
              transform: translateY(-50%);
              color: $dark;
              z-index: 5;
            }
            .input {
              padding: 5px 32px 5px 10px;
              border-radius: 4px;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

html[dir="ltr"] {
  .section-headline {

    .headline-switches {
      margin: 0 0 0 auto;

      .field {
        margin: 3px 0 3px 18px;
      }
    }
  }
}

html[dir="rtl"] {
  .section-headline {

    .headline-switches {
      
      .field {
        
        .b-tooltip {
          margin-left: 0;
          margin-right: .2rem;
        }
      }
    }
  }
}
</style>
